import {
  getTapColor,
  ListStatusBadge,
  mapAlarmGroupSeverityToLabelType,
  mapAlarmGroupStatusToLabelType,
  normalizeAlarmGroupStatus,
} from '@hakimo-ui/hakimo/feature-shared';
import { UnifiedAlarmGroup } from '@hakimo-ui/hakimo/types';
import { HourglassIcon } from '@hakimo-ui/hakimo/ui-elements';
import { Label } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';

interface Props {
  alarmGroup: UnifiedAlarmGroup;
  selected?: boolean;
  onClick?: () => void;
  getDisplayStatus?: (alarmGroupId: string, actualStatus: string) => string;
}

export function ListItem(props: Props) {
  const { alarmGroup, selected = false, onClick, getDisplayStatus } = props;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Cmd+Click (Mac) or Ctrl+Click (Windows)
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      const url = `/unified-monitoring?scan=${alarmGroup.id}`;
      window.open(url, '_blank');
      return;
    }

    onClick?.();
  };

  const tapScore = alarmGroup.tapScore ?? 70;
  const tapBgColor = getTapColor(tapScore);
  const timestampDate = new Date(alarmGroup.timestamp + 'Z');
  const displayStatus = getDisplayStatus
    ? getDisplayStatus(alarmGroup.id, alarmGroup.status)
    : alarmGroup.status;

  return (
    <button
      onClick={handleClick}
      className={clsx(
        'relative flex w-full cursor-pointer items-center gap-4 rounded-md px-4 py-1 outline-none',
        selected
          ? 'bg-onlight-bg-3 dark:bg-ondark-bg-3'
          : 'hover:bg-onlight-bg-3/50 dark:hover:bg-ondark-bg-3/50',
        'animate-highlight dark:animate-dark-highlight'
      )}
    >
      <div className="flex w-full items-stretch">
        <div
          className="font-oswald flex w-16 flex-shrink-0 items-center justify-center text-center text-3xl font-light text-white"
          style={{ backgroundColor: tapBgColor || 'inherit' }}
        >
          {tapBgColor ? (
            <span>{tapScore}</span>
          ) : (
            <span className="inline-block h-5 w-5 flex-shrink-0">
              <HourglassIcon />
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col truncate px-4 py-2">
          <span
            className="text-onlight-text-1 dark:text-ondark-text-1 flex items-center justify-between truncate text-sm font-medium"
            title={alarmGroup.location?.name || alarmGroup.tenantId}
          >
            <span className="overflow-hidden text-ellipsis">
              {alarmGroup.location?.name || alarmGroup.tenantId}
            </span>
            <Label small text={alarmGroup.alarmCount?.toString()} />
          </span>
          <div className="flex flex-col gap-1">
            <span className="dark:text-ondark-text-2 text-onlight-text-2 flex items-center gap-2 text-left text-xs">
              <span className="font-medium opacity-70">
                {timestampDate.toLocaleTimeString()}
              </span>
              <span className="opacity-70">
                {timestampDate.toLocaleDateString()}
              </span>
            </span>
            <div className="flex w-full items-center justify-between">
              <ListStatusBadge
                status={normalizeAlarmGroupStatus(displayStatus)}
                labelType={mapAlarmGroupStatusToLabelType(displayStatus)}
                iconLabelType={mapAlarmGroupSeverityToLabelType(
                  alarmGroup.severity
                )}
                iconTitle={
                  alarmGroup.severity
                    ? `${
                        alarmGroup.severity.charAt(0).toUpperCase() +
                        alarmGroup.severity.slice(1)
                      }`
                    : undefined
                }
                small
              />
              {(alarmGroup.resolution === 'escalation_open' ||
                alarmGroup.resolution === 'escalation_close') && (
                <span className="text-xs italic text-gray-400">ESCALATED</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

export default ListItem;
